import { RequestCacheStatus } from 'api/types';

import { Action } from '../actions/utils';
import { RequestState } from '.';
type StateReducer<S> = (state: S, action: Action<any>) => S;

const combineReducers =
  <S>(reducers: StateReducer<S>[]) =>
  (state: S, action: Action<any>): S =>
    reducers.reduce((acc, fn) => fn(acc, action), state);

const setGetRequestState =
  <S>(requestState: RequestState) =>
  (state: S) =>
    ({
      ...state,
      more: false,
      loading: requestState === 'request',
      cleared: false,
    } as S);

const setCacheState =
  <S>(requestCache: RequestCacheStatus, setState?: S, setLoading?: boolean) =>
  (state: S) =>
    ({
      ...(setState !== undefined ? setState : state),
      loading: setLoading !== undefined ? setLoading : (state as any).loading,
      requestCache,
    } as S);

const setCreateUpdateRequestState =
  <S>(createUpdateRequestState: RequestState) =>
  (state: S) => ({
    ...state,
    createUpdateRequestState,
  });

const setDeleteRequestState =
  <S>(deleteRequestState: RequestState) =>
  (state: S) => ({
    ...state,
    loading: deleteRequestState === 'request',
  });

const putPayload = <S>(state: S, action: Action<any>): S => {
  const commonState = {
    loading: false,
    requestState: 'success',
  };

  if (action.payload?.data && 'content' in action.payload.data) {
    return {
      ...state,
      ...action.payload.data,
      content: action.payload.data.content,
      more: action.payload.data.totalPages - 1 > action.payload.data.number,
      ...commonState,
    } as S;
  }

  if (typeof action.payload?.data !== 'undefined') {
    return {
      ...state,
      content: action.payload.data,
      ...commonState,
    } as S;
  }

  return {
    ...state,
    content: action.payload,
    ...commonState,
  } as S;
};

const putLoading = <S>(value: boolean) => {
  return (state: S): S =>
    ({
      ...state,
      loading: value,
    } as S);
};

const clearError = <S>(state: S) =>
  ({
    ...state,
    error: undefined,
  } as S);

const putError = <S>(state: S, action: Action<unknown>): S =>
  ({
    ...state,
    error: action.payload,
  } as S);

/**
 * @deprecated
 * Legacy Redux helper.
 *
 * Все новые редьюсеры лучше создавать через createSlice. Реализована похожая фабрика createAsyncSlice
 */
const genericReducers = <S>(incomeReducers: StateReducer<S> | StateReducer<S>[] = []) => {
  const reducers = Array.isArray(incomeReducers) ? incomeReducers : [incomeReducers];

  return {
    get: {
      request: combineReducers<S>([
        setGetRequestState<S>('request'),
        clearError<S>,
        setCacheState<S>('requested'),
        ...reducers,
      ]),
      success: combineReducers<S>([
        setGetRequestState<S>('success'),
        clearError<S>,
        putPayload<S>,
        setCacheState<S>('cached'),
        ...reducers,
      ]),
      failure: combineReducers<S>([
        setGetRequestState<S>('failure'),
        putError<S>,
        setCacheState<S>('failed'),
        ...reducers,
      ]),
      invalidate: (setState?: S) =>
        combineReducers<S>([setCacheState<S>('invalidated', setState, false), clearError<S>, ...reducers]),
    },

    createUpdate: {
      request: combineReducers<S>([
        setCreateUpdateRequestState<S>('request'),
        clearError<S>,
        putLoading<S>(true),
        ...reducers,
      ]),
      success: combineReducers<S>([
        setCreateUpdateRequestState<S>('success'),
        clearError<S>,
        putPayload<S>,
        putLoading<S>(false),
        ...reducers,
      ]),
      failure: combineReducers<S>([
        setCreateUpdateRequestState<S>('failure'),
        putError<S>,
        putLoading<S>(false),
        ...reducers,
      ]),
    },

    delete: {
      request: combineReducers<S>([setDeleteRequestState<S>('request'), clearError<S>, ...reducers]),
      success: combineReducers<S>([setDeleteRequestState<S>('success'), clearError<S>, putPayload<S>, ...reducers]),
      failure: combineReducers<S>([setDeleteRequestState<S>('failure'), putError<S>, ...reducers]),
    },
  };
};

export default genericReducers;
